import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import * as hr from '../api';
import { errorText } from '../../utils';
import { download, fmtNumber, titleCase, toCSV } from '../format';
import Loader from '../components/Loader';
import { MonthInput } from '../components/Controls';

// Grouped totals over one record type — the port of Stephanet's report view.
// attendance: per employee for a month (records over the chosen range); id_cards: by template and status.

const iso = d => d.toISOString().slice(0, 10);
const monthRange = m => {
  const [y, mo] = m.split('-').map(Number);
  return { start_date: `${m}-01`, end_date: iso(new Date(Date.UTC(y, mo, 0))) };
};

const REPORTS = {
  attendance: {
    title: 'Attendance by Employee',
    period: 'month',
    load: async month => hr.listAttendanceRecords(monthRange(month)),
    group: r => `${r.full_name || '—'} (${r.employee_no || '—'})`,
    groupLabel: 'Employee',
    columns: [
      { name: 'days', label: 'Days Recorded', sum: () => 1 },
      { name: 'present', label: 'Present', sum: r => (r.status === 'present' ? 1 : 0) },
      { name: 'absent', label: 'Absent', sum: r => (r.status === 'absent' ? 1 : 0) },
      { name: 'late', label: 'Late', sum: r => (r.is_late ? 1 : 0) },
      { name: 'hours_worked', label: 'Hours Worked', sum: r => Number(r.hours_worked) || 0 },
      { name: 'overtime_hours', label: 'Overtime Hours', sum: r => Number(r.overtime_hours) || 0 },
    ],
    sortBy: 'overtime_hours',
  },
  id_cards: {
    title: 'ID Cards by Template',
    load: async () => hr.listIdCards(),
    group: r => titleCase(r.template_type || 'standard'),
    groupLabel: 'Template',
    columns: ['requested', 'printed', 'active', 'expired', 'lost'].map(s => ({ name: s, label: titleCase(s), sum: r => (r.status === s ? 1 : 0) }))
      .concat([{ name: 'total', label: 'Total Cards', sum: () => 1 }]),
    sortBy: 'total',
  },
};

export default function Report({ report }) {
  const spec = REPORTS[report];
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setRows(null); setError('');
    spec.load(month).then(setRows).catch(e => { setError(errorText(e)); setRows([]); });
  }, [spec, month]);

  const grouped = useMemo(() => {
    const m = new Map();
    (rows || []).forEach(r => {
      const k = spec.group(r);
      const acc = m.get(k) || Object.fromEntries(spec.columns.map(c => [c.name, 0]));
      spec.columns.forEach(c => { acc[c.name] += c.sum(r); });
      m.set(k, acc);
    });
    return [...m.entries()].map(([group, v]) => ({ group, ...v })).sort((a, b) => b[spec.sortBy] - a[spec.sortBy]);
  }, [rows, spec]);
  const totals = useMemo(() => Object.fromEntries(spec.columns.map(c => [c.name, grouped.reduce((t, g) => t + g[c.name], 0)])), [grouped, spec]);

  const exportCSV = () => download(`${report}-report.csv`, toCSV(
    [{ label: spec.groupLabel, get: r => r.group }, ...spec.columns.map(c => ({ label: c.label, get: r => r[c.name] }))], grouped));

  return (
    <div className="panel">
      <div className="toolbar">
        <h3 className="panel-title">{spec.title}</h3>
        <div className="spacer" />
        {spec.period === 'month' && <MonthInput value={month} onChange={e => e.target.value && setMonth(e.target.value)} />}
        <button className="btn ghost" onClick={exportCSV} disabled={!grouped.length}><Download size={15} /> Export</button>
      </div>
      {error && <div className="error">{error}</div>}
      <div className="table-wrap">
        <table>
          <thead><tr><th>{spec.groupLabel}</th>{spec.columns.map(c => <th key={c.name} className="num">{c.label}</th>)}</tr></thead>
          <tbody>
            {grouped.map(g => (
              <tr key={g.group}><td>{g.group}</td>{spec.columns.map(c => <td key={c.name} className="num">{fmtNumber(g[c.name])}</td>)}</tr>
            ))}
            {rows === null && <tr><td colSpan={spec.columns.length + 1}><Loader title={`Loading ${spec.title}…`} /></td></tr>}
            {rows !== null && !grouped.length && <tr><td colSpan={spec.columns.length + 1} className="empty">No records{spec.period === 'month' ? ' in this month' : ''}.</td></tr>}
          </tbody>
          {grouped.length > 0 && (
            <tfoot><tr><td>TOTAL</td>{spec.columns.map(c => <td key={c.name} className="num">{fmtNumber(totals[c.name])}</td>)}</tr></tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
